import axios from 'axios';
import { handleSessionExpired, tryRefreshToken } from '../utils/sessionManager';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/v1';

const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses - try to refresh token first
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If 401 and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Try to refresh the token
      const newToken = await tryRefreshToken();
      
      if (newToken) {
        // Retry the original request with the new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      }
      
      // Refresh failed, session is expired
      handleSessionExpired();
    }
    return Promise.reject(error);
  }
);

/**
 * Create a new price record
 * @param {Object} priceData - The price data to save
 * @returns {Promise<Object>} The created price record
 */
export const createPrice = async (priceData) => {
  try {
    const response = await api.post('/prices', priceData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get all prices for the current user
 * @param {Object} options - Query options (sortBy, limit, page)
 * @returns {Promise<Object>} Paginated list of prices
 */
export const getPrices = async (options = {}) => {
  try {
    const response = await api.get('/prices', { params: options });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Get a specific price by ID
 * @param {string} priceId - The price ID
 * @returns {Promise<Object>} The price record
 */
export const getPrice = async (priceId) => {
  try {
    const response = await api.get(`/prices/${priceId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Update a price record
 * @param {string} priceId - The price ID
 * @param {Object} updateData - The data to update
 * @returns {Promise<Object>} The updated price record
 */
export const updatePrice = async (priceId, updateData) => {
  try {
    const response = await api.patch(`/prices/${priceId}`, updateData);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

/**
 * Delete a price record
 * @param {string} priceId - The price ID
 * @returns {Promise<void>}
 */
export const deletePrice = async (priceId) => {
  try {
    await api.delete(`/prices/${priceId}`);
  } catch (error) {
    throw error.response?.data || error;
  }
};

export default {
  createPrice,
  getPrices,
  getPrice,
  updatePrice,
  deletePrice,
};
